import { PAYMENT_METHOD } from 'src/app/shared/enums/payment-method.enum';
import { PAYMENT_TYPE } from 'src/app/shared/enums/payment-type.enum';
import { TRACKER_TYPE } from '../enums/tracker.enum';
import { ITrackerPaymentBody } from '../interfaces/payment-tracker.interface';
import { TrackingService } from '../tracking.service';

/**
 * Options for payment tracking
 */
export interface IPaymentTrackingOptions {
  paymentId?: string;
  accountId?: string;
  paymentMethod?: PAYMENT_METHOD;
  paymentType?: PAYMENT_TYPE;
  amount?: number;
  customData?: unknown;
  flow?: string;
  entriesCount?: number;
  isAccountClosed?: boolean;
  isTicketGenerated?: boolean;
  error?: string;
  success?: boolean;
}

/**
 * Creates a payment tracking function that doesn't depend on inject()
 *
 * This factory creates a function that can be used outside injection context
 * since it already receives the TrackingService instance as parameter.
 *
 * @param trackingService TrackingService instance
 * @returns A function that can be used to track payment events
 *
 * @example
 * // In a service constructor or field initialization:
 * private readonly trackPayment = createPaymentTracker(this.trackingService);
 *
 * // Then use it anywhere in your class:
 * this.trackPayment('Payment processed', {
 *   paymentId: 'pay-123',
 *   amount: 25.50,
 *   paymentMethod: PAYMENT_METHOD.card
 * });
 */
export function createPaymentTracker(trackingService: TrackingService) {
  /**
   * Records payment-related activity for tracking purposes.
   *
   * @param message - Description of the payment activity
   * @param options - Optional payment-specific tracking options
   */
  return function trackPayment(message: string, options?: IPaymentTrackingOptions): void {
    const body: ITrackerPaymentBody = {
      message,
      ...options,
    };

    trackingService.addTrack({
      type: TRACKER_TYPE.payment,
      body,
    });
  };
}
